

import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MessageSquare, 
  FileText, 
  Calendar, 
  Users, 
  CheckSquare, 
  Building2, 
  Package,
  Boxes,
  LogOut,
  X,
  Settings,
  BarChart3,
  Tags,
  Wallet,
  Briefcase,
  UserCog,
  UserCircle,
  CreditCard,
  MapPin,
  CalendarDays,
  Banknote,
  ChevronDown,
  Globe,
  Database,
  BedDouble,
  Activity,
  ReceiptIndianRupee,
  Monitor,
  PanelLeft,
  TerminalSquare,
} from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { usePermission } from "@/context/PermissionContext";
import { getImageUrl } from "@/lib/utils";
import { useState, useMemo, useEffect } from "react";
import { MAIN_URL } from "@/lib/api";

interface NavItem {
  page: string;
  label: string;
  href: string;
  icon: any;
  badge?: number;
  /** Only these roles see the link (instead of the page-access switches) */
  roles?: string[];
}

interface NavGroup {
  title: string;
  icon: any;
  items: NavItem[];
}

interface SidebarProps {
  userName: string;
  userRole?: string;
  onLogout: () => void;
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}


// ── Daily Operations (flat, always visible) ─────────────────────────────────
const topNavItems: NavItem[] = [
  { page: "dashboard",  label: "Dashboard",  href: "/dashboard",  icon: LayoutDashboard },
  { page: "enquiries",  label: "Enquiries",  href: "/enquiries",  icon: MessageSquare },
  { page: "quotations", label: "Quotations", href: "/quotations", icon: FileText },
  { page: "events",     label: "Events",     href: "/events",     icon: Calendar },
  { page: "clients",    label: "Clients",    href: "/clients",    icon: UserCircle },
  { page: "calendar",   label: "Calendar",   href: "/calendar",   icon: CalendarDays },
];

// ── Collapsible Groups ───────────────────────────────────────────────────────
const navGroups: NavGroup[] = [
  {
    title: "Finance",
    icon: Wallet,
    items: [
      { page: "expenses",        label: "Expenses",        href: "/expenses",        icon: Wallet },
      { page: "vendor-payments", label: "Vendor Payments", href: "/vendor-payments", icon: CreditCard },
    ],
  },
  {
    title: "Planning",
    icon: CheckSquare,
    items: [
      { page: "tasks",   label: "Tasks",   href: "/tasks",   icon: CheckSquare },
      { page: "reports", label: "Reports", href: "/reports", icon: BarChart3 },
    ],
  },
  {
    title: "Catalog",
    icon: Boxes,
    items: [
      { page: "packages", label: "Packages",        href: "/packages",  icon: Boxes },
      { page: "services", label: "Service Catalog", href: "/services",  icon: Package },
    ],
  },
  {
    title: "Venue & Rooms",
    icon: Building2,
    items: [
      { page: "venues",        label: "Venues",        href: "/venues",        icon: MapPin },
      { page: "rooms",         label: "Rooms",         href: "/rooms",         icon: Building2 },
      { page: "room-bookings", label: "Room Bookings", href: "/room-bookings", icon: BedDouble },
    ],
  },
  {
    title: "People",
    icon: Users,
    items: [
      { page: "staff",   label: "Staff",   href: "/staff",   icon: UserCog },
      { page: "vendors", label: "Vendors", href: "/vendors", icon: Users },
    ],
  },
];

// ── Admin-only: Settings + Master Data + System ──────────────────────────────
const systemGroup: NavGroup = {
  title: "Admin",
  icon: Settings,
  items: [
    { page: "settings",           label: "Settings",           href: "/settings",           icon: Settings },
    { page: "event-types",        label: "Event Types",        href: "/event-types",        icon: CalendarDays },
    { page: "vendor-types",       label: "Vendor Categories",  href: "/vendor-types",       icon: Briefcase },
    { page: "payment-methods",    label: "Payment Methods",    href: "/payment-methods",    icon: Banknote },
    { page: "expense-categories", label: "Expense Categories", href: "/expense-categories", icon: Tags },
    { page: "frontend-settings",  label: "Site Settings",      href: "/frontend-settings",  icon: Globe },
    { page: "activity-logs",      label: "Activity Logs",      href: "/activity-logs",      icon: Activity },
    { page: "payment-logs",       label: "Payment Logs",       href: "/payment-logs",       icon: ReceiptIndianRupee },
    { page: "system-console",     label: "System Console",     href: "/system-console",     icon: TerminalSquare, roles: ["super_admin", "master"] },
  ],
};

function NavLink({ item, isActive, onClick, isCollapsed = false }: { item: NavItem; isActive: boolean; onClick: () => void; isCollapsed?: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
        isCollapsed 
          ? `justify-center p-2 ${isActive ? 'bg-amber-100 text-amber-700' : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'}`
          : `px-2.5 py-2 ${
              isActive
                ? "bg-gradient-to-r from-amber-50 to-white text-amber-800 shadow-sm border border-amber-100/50"
                : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
            }`
      }`}
      title={isCollapsed ? item.label : undefined}
    >
      <div className={`p-1 rounded-md transition-colors ${isActive ? "bg-amber-100/80 text-amber-700" : "bg-transparent text-stone-400 group-hover:text-stone-600"}`}>
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2.5 : 2} />
      </div>
      {!isCollapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

function NavGroupSection({ group, pathname, onLinkClick }: { group: NavGroup | null; pathname: string; onLinkClick: () => void }) {
  if (!group) return null;
  
  const hasActiveItem = group.items.some(item => pathname.startsWith(item.href) && item.href !== '/dashboard');
  const [isOpen, setIsOpen] = useState(hasActiveItem);
  const GroupIcon = group.icon;
  
  return (
    <div className="mb-0.5">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium transition-all group ${
          hasActiveItem 
            ? "text-amber-800" 
            : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1 rounded-md transition-colors ${hasActiveItem ? "bg-amber-100/50 text-amber-600" : "bg-transparent text-stone-400 group-hover:text-stone-600"}`}>
            <GroupIcon className="w-4 h-4 flex-shrink-0" strokeWidth={hasActiveItem ? 2.5 : 2} />
          </div>
          <span className="truncate font-semibold">{group.title}</span>
        </div>
        <div className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
          <ChevronDown className="w-3.5 h-3.5 text-stone-400" />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-[800px] opacity-100 mt-0.5" : "max-h-0 opacity-0"
        }`}
      >
        <div className="space-y-0.5 border-l-2 border-stone-100/60 ml-[21px] pl-2.5 py-0.5 mb-1">
          {group.items.map((item) => (
            <NavLink
              key={item.page}
              item={item}
              isActive={pathname.startsWith(item.href) && item.href !== '/dashboard'}
              onClick={onLinkClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ userName, userRole, onLogout, isOpen, onClose, isCollapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  
  // Role-based permissions (DB-driven via PermissionContext)
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const { can: canViewPageDb, loading: permsLoading } = usePermission();

  const { settings } = useSettings();
  
  // Dedicated logo state - fetched from lightweight endpoint
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState('Shalimar ERP');
  
  // Fetch logo from dedicated lightweight endpoint
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch(`${MAIN_URL}/api/settings/logo`);
        if (response.ok) {
          const data = await response.json();
          console.log('Sidebar - Logo API Response:', data);
          
          if (data?.success && data?.data) {
            console.log('Sidebar - Raw logo URL from API:', data.data.logoUrl);
            
            const fullLogoUrl = data.data.logoUrl ? getImageUrl(data.data.logoUrl) : null;
            console.log('Sidebar - Converted full logo URL:', fullLogoUrl);
            
            setLogoUrl(fullLogoUrl);
            if (data.data.businessName) {
              setBusinessName(data.data.businessName);
            }
          }
        } else {
          console.warn('Sidebar - Logo endpoint returned status:', response.status);
        }
      } catch (error) {
        console.error('Sidebar - Failed to fetch logo:', error);
      }
    };
    
    fetchLogo();
  }, []);
  
  // Fallback to SettingsContext if available
  const finalLogoUrl = logoUrl || (settings?.logoUrl ? getImageUrl(settings.logoUrl) : null);
  const finalBusinessName = businessName || settings?.business?.businessName || 'Shalimar ERP';

  const handleLinkClick = () => {
    if (onClose && typeof window !== 'undefined' && window.innerWidth < 1024) {
      onClose();
    }
  };

  // DB-driven access check with admin always-allowed fallback during initial load
  const canViewPage = (page: string): boolean => {
    if (isAdmin) return true;
    if (permsLoading) return false;
    return canViewPageDb(page);
  };

  // Filter navigation items based on DB-driven access
  const filteredTopNavItems = useMemo(() => {
    return topNavItems.filter(item => canViewPage(item.page));
  }, [isAdmin, permsLoading, canViewPageDb]);

  const filteredNavGroups = useMemo(() => {
    return navGroups.map(group => {
      const filteredItems = group.items.filter(item => canViewPage(item.page));
      return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);
  }, [isAdmin, permsLoading, canViewPageDb]);

  const filteredSystemGroup = useMemo(() => {
    const filteredItems = systemGroup.items.filter(item =>
      item.roles ? item.roles.includes(userRole ?? '') : canViewPage(item.page));
    return filteredItems.length > 0 ? { ...systemGroup, items: filteredItems } : null;
  }, [isAdmin, permsLoading, canViewPageDb, userRole]);

  return (
    <aside 
      className={`
        bg-white/95 backdrop-blur-xl border-r border-stone-200/60 shadow-sm fixed h-full flex flex-col z-50
        transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Compact Header */}
      <div className="p-3 border-b border-stone-100/80 flex-shrink-0">
        <div className="flex items-center gap-2">
          {finalLogoUrl ? (
            <img 
              src={finalLogoUrl} 
              alt={finalBusinessName}
              className="w-6 h-6 rounded-md object-cover flex-shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              S
            </div>
          )}
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <h1 className="font-bold text-stone-800 text-sm truncate">{finalBusinessName}</h1>
            </div>
          )}
          {/* Collapse Toggle - Desktop Only */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <PanelLeft className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`} />
          </button>
          {/* Mobile Close */}
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable Navigation Area */}
      <nav className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
        {/* Daily Operations */}
        <div className={`flex-none ${isCollapsed ? 'p-2' : 'p-2.5'}`}>
          {!isCollapsed && (
            <p className="px-2 pt-1 pb-1.5 text-[10px] font-bold text-stone-400 uppercase tracking-widest">
              Operations
            </p>
          )}
          <div className={`${isCollapsed ? 'space-y-1.5' : 'space-y-0.5'}`}>
            {filteredTopNavItems.map((item) => (
              <NavLink
                key={item.page}
                item={item}
                isActive={location.pathname === item.href || (item.href !== '/dashboard' && location.pathname.startsWith(item.href))}
                onClick={handleLinkClick}
                isCollapsed={isCollapsed}
              />
            ))}
          </div>
        </div>

        {/* Collapsible Groups */}
        {isCollapsed ? (
          /* Collapsed: show first icon of each group with tooltip */
          <div className="flex-none p-2 space-y-2 border-t border-stone-100/80 mt-1 pt-3">
            {[...filteredNavGroups, ...(filteredSystemGroup ? [filteredSystemGroup] : [])].map((group) => {
              const hasActive = group.items.some(item => location.pathname.startsWith(item.href) && item.href !== '/dashboard');
              const GroupIcon = group.icon;
              return (
                <div key={group.title} className="relative group/grp">
                  <div className={`flex justify-center p-2 rounded-lg cursor-default ${hasActive ? 'bg-amber-100 text-amber-700' : 'text-stone-400 hover:bg-stone-50 hover:text-stone-600'}`}>
                    <GroupIcon className="w-4 h-4 flex-shrink-0" strokeWidth={hasActive ? 2.5 : 2} />
                  </div>
                  {/* Tooltip flyout */}
                  <div className="absolute left-full top-0 ml-2 z-50 hidden group-hover/grp:block">
                    <div className="bg-white border border-stone-200 rounded-xl shadow-lg p-2 min-w-[180px]">
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider px-2 pb-1.5">{group.title}</p>
                      {group.items.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = location.pathname.startsWith(item.href) && item.href !== '/dashboard';
                        return (
                          <Link
                            key={item.page}
                            to={item.href}
                            onClick={handleLinkClick}
                            className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-amber-50 text-amber-700 font-medium' : 'text-stone-600 hover:bg-stone-50'}`}
                          >
                            <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <div className="flex-none px-2.5 pb-2.5">
              <div className="border-t border-stone-100 pt-2.5">
                <p className="px-2 pb-1.5 text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                  More
                </p>
                <div className="space-y-0.5">
                  {filteredNavGroups.map((group) => (
                    <NavGroupSection
                      key={group.title}
                      group={group}
                      pathname={location.pathname}
                      onLinkClick={handleLinkClick}
                    />
                  ))}
                </div>
              </div>

              {filteredSystemGroup && (
                <div className="border-t border-stone-100 mt-2 pt-2.5">
                  <p className="px-2 pb-1.5 text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                    Admin
                  </p>
                  <NavGroupSection
                    group={filteredSystemGroup}
                    pathname={location.pathname}
                    onLinkClick={handleLinkClick}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </nav>

      {/* User Profile - Fixed at bottom */}
      <div className={`flex-shrink-0 border-t border-stone-100/80 bg-white z-10 ${isCollapsed ? 'p-2' : 'p-2.5'}`}>
        <div className={`group flex items-center bg-white border border-stone-200/60 shadow-sm rounded-lg hover:shadow-md hover:border-stone-300/60 transition-all cursor-pointer ${isCollapsed ? 'justify-center p-2' : 'gap-2 p-2'}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-700 font-bold text-xs shadow-inner overflow-hidden border border-amber-300/50 flex-shrink-0" title={userName}>
            {userName?.charAt(0) || "U"}
          </div>
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-stone-800 truncate leading-tight group-hover:text-amber-700 transition-colors">{userName}</p>
                <p className="text-xs text-stone-500 capitalize truncate font-medium">{userRole}</p>
              </div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onLogout();
                }}
                className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all active:scale-95"
                title="Logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={2} />
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
